import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase owns identity only — accounts, providers, sessions.
 *
 * It is NOT the engine's store: the semantic graph and every session stay where
 * they are (in memory, persisted to logs/<sessionId>/session-state.json). The
 * only things that need a real database are the people.
 *
 * The anon key is public by design — it is safe in the bundle. Everything that
 * matters is enforced server-side: Heimar.Api validates the access token and
 * reads the account's status itself, so a tampered client gets nothing.
 */
const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

/**
 * Null when no project is configured. Local development then runs OPEN — the
 * server treats an unauthenticated caller as an accepted admin so every surface
 * is reachable without standing up Supabase. Production always configures it,
 * and the server refuses to run open when it is in production.
 */
export const supabase: SupabaseClient | null =
  url && anonKey
    ? createClient(url, anonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          // Both magic-link and OAuth callbacks land back on `/` carrying the
          // session in the URL; this is what picks it up. Supabase's Site URL
          // must be heimar.ai with localhost merely ALLOWED — the other way
          // round sends production callbacks to localhost.
          detectSessionInUrl: true,
        },
      })
    : null

export const authConfigured = supabase !== null

/** The bearer token for calls into Heimar.Api, or null when signed out. */
export async function accessToken(): Promise<string | null> {
  if (!supabase) return null
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}
